// import { gettext } from '@eventbrite/i18n';
import {
    categoryCodeToReadableTag,
    EVENTBRITE_FORMAT_MAPPING_PLURAL,
    PARTY_EVENTBRITE_FORMAT,
    PERFORMANCE_EVENTBRITE_FORMAT,
} from '@eventbrite/categories';
import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import { StickyContainer } from 'react-sticky';
import { SeasonalRibbon } from '../../../components/SeasonalRibbon';
import { ACTIVE_SEASON } from '../../../constants';
import { LazyRenderer } from '../components/LazyRenderer';
import { NightlifeBackground } from '../components/NightlifeBackground';
import { FREE, MOST_POPULAR, THIS_WEEKEND } from '../constants/bucketKey';
import { NIGHTLIFE_TAGS } from '../constants/nightLife';
import { CategoryBrowseBreadCrumb } from '../containers/CategoryBrowseBreadCrumb';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import CategoryBrowseNearbyCities from '../containers/CategoryBrowseNearbyCities';
import { NightlifeCategoriesContainer } from '../containers/NightlifeCategoriesContainer';
import { NightlifeHeaderContainer } from '../containers/NightlifeHeaderContainer';
import { TrendingTagEvents } from '../containers/TrendingTagEvents';
import { useEnvContext, useRouteContext, useSearchContext } from '../context';
import { useLocation } from '../hooks/queries/location';
import { NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT } from '../utils/analytics';
import { getLanguageSubdirectorySubPath } from '../utils/localeSubPath';
import { categoryBrowseBucketsNewAdsExperiment } from './Experiments';
import './NightlifePage.scss';

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);

//TODO: Set date filter to leverage current future and date when both applied
export const NightlifePage: React.FunctionComponent = () => {
    // Env and General Data
    const envContext: any = useEnvContext();
    const { query } = useSearchContext();
    const { parsedValues, location } = useRouteContext();
    const locationData = useLocation();
    const queryActiveTags = query?.tags || NIGHTLIFE_TAGS;
    const isPartyCategory =
        query?.tags?.length === 1 && query.tags[0] === PARTY_EVENTBRITE_FORMAT;
    const isWeekendFilter = query?.dates?.includes('this_weekend');
    const user = envContext?.user;
    const locale = envContext?.env?.localeInfo?.locale || 'en_US';
    const isBotRequest = envContext?.isBotRequest;
    const activeTag = parsedValues?.activeTag || '';
    const date = parsedValues?.date;
    const customDate = parsedValues?.customDate;
    const shouldRenderThisWeekend = () => {
        return date === 'current_future' && !isWeekendFilter && !customDate;
    };
    const placeId = locationData.data?.placeId || '';

    const adSlotsExp =
        categoryBrowseBucketsNewAdsExperiment() === 'ad1' ? [1] : [];

    const renderEventCarousels = () => {
        return (
            <div className="event_carousel">
                <CategoryBrowseEvents
                    queryOverload={{
                        page_size: 25,
                        price: 'paid',
                    }}
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketKey={MOST_POPULAR}
                    bucketPosition="2"
                    adSlots={[1]}
                    renderEmptyState
                />

                {shouldRenderThisWeekend() && (
                    <CategoryBrowseEvents
                        queryOverload={{
                            page_size: 25,
                            dates: ['this_weekend'],
                            price: 'paid',
                            aggs: {},
                        }}
                        title={gettext('This weekend')}
                        bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                        bucketKey={THIS_WEEKEND}
                        bucketPosition="4"
                        lazyLoadCarouselElements
                        adSlots={adSlotsExp}
                    />
                )}
                {!isPartyCategory && (
                    <CategoryBrowseEvents
                        queryOverload={{
                            page_size: 25,
                            tags: [PARTY_EVENTBRITE_FORMAT, ...queryActiveTags],
                            price: 'paid',
                            aggs: {},
                        }}
                        title={
                            EVENTBRITE_FORMAT_MAPPING_PLURAL[
                                PARTY_EVENTBRITE_FORMAT
                            ]
                        }
                        bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                        bucketKey={
                            categoryCodeToReadableTag(
                                PARTY_EVENTBRITE_FORMAT,
                            ) || PARTY_EVENTBRITE_FORMAT
                        }
                        bucketPosition="6"
                        forceRenderHeader={isBotRequest}
                        lazyLoadCarouselElements
                        adSlots={adSlotsExp}
                    />
                )}
                <CategoryBrowseEvents
                    forceRenderHeader={isBotRequest}
                    queryOverload={{
                        page_size: 25,
                        price: 'free',
                        aggs: {},
                    }}
                    title={gettext('Free')}
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketKey={FREE}
                    bucketPosition="5"
                    lazyLoadCarouselElements
                    adSlots={adSlotsExp}
                />
                <TrendingTagEvents
                    title={gettext('Trending Tags')}
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketKey="trending_tag_events"
                    bucketPosition="7"
                    placeId={placeId}
                    isNightlife={true}
                    lazyLoadCarouselElements
                />
                <CategoryBrowseEvents
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketPosition="8"
                    queryOverload={{
                        page_size: 25,
                        tags: [
                            PERFORMANCE_EVENTBRITE_FORMAT,
                            ...queryActiveTags,
                        ],
                        price: 'paid',
                        aggs: {},
                    }}
                    title={
                        EVENTBRITE_FORMAT_MAPPING_PLURAL[
                            PERFORMANCE_EVENTBRITE_FORMAT
                        ]
                    }
                    bucketKey={
                        categoryCodeToReadableTag(
                            PERFORMANCE_EVENTBRITE_FORMAT,
                        ) || PERFORMANCE_EVENTBRITE_FORMAT
                    }
                    forceRenderHeader={isBotRequest}
                    lazyLoadCarouselElements
                    adSlots={adSlotsExp}
                />
                <CategoryBrowseOrganizers
                    user={user}
                    location={location}
                    isNightlifePage={true}
                />
                <CategoryBrowseNearbyCities customClassName="near_by_cities" />
            </div>
        );
    };

    // todo update parties and  today , weekend
    return (
        <div className="nightlife">
            <NightlifeBackground>
                <StickyContainer style={{ overflowY: 'auto' }}>
                    <SeasonalRibbon
                        season={ACTIVE_SEASON}
                        locationSlug={
                            envContext?.request?.default_location_for_tld
                                ?.location_slug || 'online'
                        }
                        country={
                            envContext?.request?.default_location_for_tld
                                ?.country
                        }
                        browseSurface={'category-browse'}
                        isMobile={envContext?.env?.isMobile}
                    />
                    <CategoryBrowseBreadCrumb
                        languageSubdirectory={getLanguageSubdirectorySubPath(
                            envContext,
                        )}
                    />

                    <main className="nightlife__main">
                        <NightlifeHeaderContainer
                            activeCategory={activeTag}
                            locale={locale}
                        />
                        <StickyContainer style={{ overflowY: 'auto' }}>
                            <LazyRenderer
                                numChildInitialRender={isBotRequest ? -1 : 2}
                            >
                                <div className="nightlife-horizontal-bar"></div>
                                <NightlifeCategoriesContainer />

                                {renderEventCarousels()}
                            </LazyRenderer>
                        </StickyContainer>
                    </main>
                </StickyContainer>
            </NightlifeBackground>
        </div>
    );
};

export default NightlifePage;
